import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { logger } from '@/ui'
import type { ConversationWindow } from '@/ui/views/campaign-card/candidate-card/whatsapp-thread'
import { mapApiConversationWindow } from '@/ui/views/campaign-card/candidate-card/whatsapp-thread'
import {
	candidateWhatsAppIndexQueryKey,
	pipelineStageShowQueryKey,
	useCandidateWhatsAppIndex,
	useCandidateWhatsAppMarkAllAsRead,
	useCandidateWhatsAppMarkAsRead,
	useCandidateWhatsAppStore,
	whatsAppAccountInactivityQueryKey,
	whatsAppConversationIndexQueryKey,
	whatsAppEmbeddedSignupStatusQueryKey,
} from '@jobilla/nexus-sdk'
import type {
	CandidateWhatsAppIndex403,
	CandidateWhatsAppIndexResponse,
	CandidateWhatsAppMarkAsReadMutationResponse,
	CandidateWhatsAppMessageData,
	CandidateWhatsAppStore400,
	CandidateWhatsAppStore403,
	CandidateWhatsAppStore422,
	CandidateWhatsAppStoreMutationResponse,
	WhatsAppConversationIndexResponse,
} from '@jobilla/nexus-sdk'
import * as Sentry from '@sentry/react'
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

type OptimisticContext = { previousResponse: CandidateWhatsAppIndexResponse | undefined }

export type UseCandidateWhatsAppReturn = {
	messages: CandidateWhatsAppMessageData[]
	conversationWindow: ConversationWindow | null
	isLoading: boolean
	error: CandidateWhatsAppIndex403 | null
	refetch: (
		options?: RefetchOptions,
	) => Promise<QueryObserverResult<CandidateWhatsAppIndexResponse, CandidateWhatsAppIndex403>>
	sendTemplateMessage: (templateId: string, parameters?: string[]) => Promise<CandidateWhatsAppStoreMutationResponse>
	sendTextMessage: (content: string, mediaFile?: File) => Promise<CandidateWhatsAppStoreMutationResponse>
	isSending: boolean
	sendError: CandidateWhatsAppStore400 | CandidateWhatsAppStore403 | CandidateWhatsAppStore422 | null
	markAsRead: (messageIds?: string[]) => Promise<CandidateWhatsAppMarkAsReadMutationResponse>
	markAllAsRead: () => Promise<unknown>
	isMarkingRead: boolean
}

export function useCandidateWhatsApp(
	candidateId: string,
	campaignId?: string,
	trackingSource: 'inbox' | 'candidate_card' = 'candidate_card',
): UseCandidateWhatsAppReturn {
	const queryClient = useQueryClient()
	const { getAccessToken } = useAuth()
	const organisationId = useJobillaOrgId()

	const messageQueryKey = candidateWhatsAppIndexQueryKey(candidateId)

	const invalidateMessageQueries = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: messageQueryKey })
		// Broad key prefix-matches every filter variant of the inbox conversation list,
		// so replying/marking-read updates needs-reply and unread state there too.
		void queryClient.invalidateQueries({ queryKey: whatsAppConversationIndexQueryKey() })
		if (campaignId) {
			void queryClient.invalidateQueries({ queryKey: pipelineStageShowQueryKey(campaignId) })
		}
	}, [queryClient, messageQueryKey, campaignId])

	const invalidateInactivityQueries = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: whatsAppAccountInactivityQueryKey(organisationId) })
		void queryClient.invalidateQueries({ queryKey: whatsAppEmbeddedSignupStatusQueryKey(organisationId) })
	}, [queryClient, organisationId])

	const invalidateSentMessageQueries = useCallback(() => {
		invalidateMessageQueries()
		invalidateInactivityQueries()
	}, [invalidateMessageQueries, invalidateInactivityQueries])

	const createOptimisticContext = useCallback(async (): Promise<OptimisticContext> => {
		await queryClient.cancelQueries({ queryKey: messageQueryKey })
		return { previousResponse: queryClient.getQueryData<CandidateWhatsAppIndexResponse>(messageQueryKey) }
	}, [queryClient, messageQueryKey])

	const rollbackOnError = useCallback(
		(_err: unknown, _variables: unknown, context: unknown) => {
			const ctx = context as OptimisticContext | undefined
			if (ctx?.previousResponse) {
				queryClient.setQueryData(messageQueryKey, ctx.previousResponse)
			}
		},
		[queryClient, messageQueryKey],
	)

	const updateMessagesOptimistically = useCallback(
		(
			predicate: (message: CandidateWhatsAppMessageData) => boolean,
			update: Partial<CandidateWhatsAppMessageData>,
		) => {
			queryClient.setQueryData<CandidateWhatsAppIndexResponse>(messageQueryKey, (old) => {
				if (!old) {
					return old
				}
				return {
					...old,
					data: old.data.map((
						message: CandidateWhatsAppMessageData,
					) => (predicate(message) ? { ...message, ...update } : message)),
				}
			})
		},
		[queryClient, messageQueryKey],
	)

	// Optimistically settle read state into every cached conversation-list variant so
	// the inbox rows and the nav badge react instantly; the onSettled invalidation
	// refetches the authoritative counts afterwards.
	const updateConversationListOptimistically = useCallback(
		(markedMessageIds?: string[]) => {
			queryClient.setQueriesData<WhatsAppConversationIndexResponse>(
				{ queryKey: whatsAppConversationIndexQueryKey() },
				(old) => {
					if (!old) {
						return old
					}

					let conversationBecameRead = false
					const data = old.data.map((row) => {
						if (row.candidateId !== candidateId || row.unreadCount === 0) {
							return row
						}
						const remainingUnread = markedMessageIds
							? Math.max(0, row.unreadCount - markedMessageIds.length)
							: 0
						if (remainingUnread === 0) {
							conversationBecameRead = true
						}
						return { ...row, unreadCount: remainingUnread }
					})

					return {
						...old,
						data,
						meta: {
							...old.meta,
							unreadConversationsCount: conversationBecameRead
								? Math.max(0, old.meta.unreadConversationsCount - 1)
								: old.meta.unreadConversationsCount,
						},
					}
				},
			)
		},
		[queryClient, candidateId],
	)

	const messagesQuery = useCandidateWhatsAppIndex(candidateId, {
		enabled: Boolean(candidateId),
		refetchInterval: 10_000,
	})

	const sendMessageMutation = useCandidateWhatsAppStore({
		mutationKey: ['candidate-whatsapp-send', candidateId],
		onSuccess: invalidateSentMessageQueries,
	})

	const markAsReadMutation = useCandidateWhatsAppMarkAsRead({
		mutationKey: ['candidate-whatsapp-mark-read', candidateId],
		onMutate: async (variables) => {
			const context = await createOptimisticContext()
			updateMessagesOptimistically(
				(
					message,
				) => (variables?.data?.messageIds
					? variables.data.messageIds.includes(message.id)
					: message.direction === 'inbound'),
				{ isRead: true },
			)
			updateConversationListOptimistically(variables?.data?.messageIds ?? undefined)
			return context
		},
		onError: rollbackOnError,
		onSettled: invalidateMessageQueries,
	})

	const markAllAsReadMutation = useCandidateWhatsAppMarkAllAsRead({
		mutationKey: ['candidate-whatsapp-mark-all-read', candidateId],
		onMutate: async () => {
			const context = await createOptimisticContext()
			updateMessagesOptimistically((message) => message.direction === 'inbound', { isRead: true })
			updateConversationListOptimistically()
			return context
		},
		onError: rollbackOnError,
		onSettled: invalidateMessageQueries,
	})

	// Extract conversation window from response meta
	const conversationWindow = useMemo(() => {
		const apiWindow = messagesQuery.data?.meta?.conversation_window
		return apiWindow ? mapApiConversationWindow(apiWindow) : null
	}, [messagesQuery.data?.meta?.conversation_window])

	const trackMessageSent = useCallback((
		messageType: 'template' | 'text',
		data?: Record<string, unknown>,
	) => {
		logger.track('WhatsApp: Message Sent', {
			category: 'whatsapp',
			data: {
				organisation_id: organisationId,
				candidate_id: candidateId,
				message_type: messageType,
				source: trackingSource,
				...(campaignId ? { campaign_id: campaignId } : {}),
				...data,
			},
		})
	}, [organisationId, candidateId, campaignId, trackingSource])

	const sendTemplateMessage = useCallback(
		async (templateId: string, parameters?: string[]) => {
			const result = await sendMessageMutation.mutateAsync({
				candidateId,
				data: {
					messageType: 'template',
					templateId,
					parameters: parameters ?? [],
				},
			})

			trackMessageSent('template', {
				template_id: templateId,
				template_parameter_count: parameters?.length ?? 0,
			})

			return result
		},
		[sendMessageMutation, candidateId, trackMessageSent],
	)

	const sendTextMessage = useCallback(
		async (content: string, mediaFile?: File): Promise<CandidateWhatsAppStoreMutationResponse> => {
			if (mediaFile) {
				// Use FormData for file uploads
				const formData = new FormData()
				formData.append('message_type', 'text')
				formData.append('content', content)
				formData.append('media', mediaFile)

				const token = await getAccessToken()
				const response = await fetch(
					`${import.meta.env.VITE_NEXUS_API_URL}/candidates/${candidateId}/whatsapp`,
					{
						method: 'POST',
						headers: {
							'Authorization': token ? `Bearer ${token}` : '',
							'Accept': 'application/json',
							// Don't set Content-Type - browser will set it with boundary for FormData
						},
						body: formData,
					},
				)

				if (!response.ok) {
					const errorData = await response.json().catch(() => null)
					const errorMessage = errorData?.message ?? errorData?.error
						?? `Failed to send message (${response.status})`
					Sentry.captureMessage('WhatsApp message send failed', {
						level: 'error',
						tags: { component: 'useCandidateWhatsApp', action: 'sendTextMessage' },
						extra: {
							candidateId,
							status: response.status,
							errorMessage,
							hasMedia: Boolean(mediaFile),
							mediaFilename: mediaFile?.name,
						},
					})
					throw new Error(errorMessage)
				}

				const result = await response.json()
				invalidateSentMessageQueries()
				trackMessageSent('text', {
					has_media: true,
					media_type: mediaFile.type,
					message_length: content.length,
				})
				return result
			}

			// Use SDK mutation for text-only messages
			const result = await sendMessageMutation.mutateAsync({
				candidateId,
				data: {
					messageType: 'text',
					content,
				},
			})

			trackMessageSent('text', {
				has_media: false,
				message_length: content.length,
			})

			return result
		},
		[sendMessageMutation, getAccessToken, candidateId, invalidateSentMessageQueries, trackMessageSent],
	)

	return {
		messages: messagesQuery.data?.data ?? [],
		conversationWindow,
		isLoading: messagesQuery.isLoading,
		error: messagesQuery.error,
		refetch: messagesQuery.refetch,
		sendTemplateMessage,
		sendTextMessage,
		isSending: sendMessageMutation.isPending,
		sendError: sendMessageMutation.error,
		markAsRead: (messageIds?: string[]) =>
			markAsReadMutation.mutateAsync({ candidateId, data: messageIds ? { messageIds } : {} }),
		markAllAsRead: () => markAllAsReadMutation.mutateAsync({ candidateId }),
		isMarkingRead: markAsReadMutation.isPending || markAllAsReadMutation.isPending,
	}
}
